/** The part of a route match the trail is built from — `useMatches()` entries satisfy it. */
export interface BreadcrumbMatch {
  pathname: string;
  staticData: {
    breadcrumbLabel?: () => string;
    breadcrumbPath?: string;
    disableBreadcrumb?: boolean;
  };
}

export interface Crumb {
  label: string;
  href: string;
}

/**
 * One crumb per match that names itself, in match order. A match may point its crumb somewhere other
 * than its own pathname (`breadcrumbPath`), which is also what makes duplicates possible — a layout
 * and its index child both land on the parent's address, and the trail must show it once. The leaf
 * can suppress the whole trail (`disableBreadcrumb`), e.g. on an error page that is nobody's child.
 */
export function toBreadcrumbs(matches: readonly BreadcrumbMatch[]): Crumb[] {
  if (matches.at(-1)?.staticData.disableBreadcrumb) {
    return [];
  }
  const seen = new Set<string>();
  return matches.flatMap((match) => {
    const { breadcrumbLabel, breadcrumbPath } = match.staticData;
    const href = breadcrumbPath ?? match.pathname;
    if (!breadcrumbLabel || seen.has(href)) {
      return [];
    }
    seen.add(href);
    return [{ label: breadcrumbLabel(), href }];
  });
}
